import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ReactComponent as IconChangePassword } from '../../Icons/Sidebar/ChangePassword.svg';
import { ReactComponent as IconEditUser } from '../../Icons/Sidebar/EditUser.svg';
import { ReactComponent as IconLogOff } from '../../Icons/Sidebar/LogOff.svg';
import { ReactComponent as IconUser } from '../../Icons/Sidebar/User.svg';
import NavItem from './NavItem';

import { UserDTO } from 'business/models/Models';
import useModel from 'business/models/useModel';
import LicenseService from 'business/services/LicenseService';
import UserGroupService from 'business/services/UserGroupService';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { Preference } from 'components/main/GlobalPrefrences/constants';
import { Preferences } from 'components/main/GlobalPrefrences/Preferences';
import { ChangePassword } from 'components/main/userGroup';
import UserAddEdit from 'components/main/userGroup/forms/user/userAddEdit';
import RMModal from 'components/modal/RMModal';
import useLogoutUser from 'CustomHooks/useLogoutUser';
import useRMLite from 'CustomHooks/useRMLite';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { FmlxModal } from 'fmlx-common-ui';
import Cookies from 'js-cookie';
import { useRMDispatch } from 'redux/hooks';
import { useSettingsTab } from 'redux/hooks/SettingsTab';
import { useSetupTabOption } from 'redux/hooks/SetupTab';
import { setProductEdition } from 'redux/reducers/experimentReducer';
import {
  getCurrentUser,
  isAdministrator,
  removeCookie,
  RMHelpURL,
  RMLiteHelpURL,
  showAlertSnackBar,
} from 'utils/Global';
import { ReactComponent as IconExplore } from '../../Icons/Sidebar/Explore.svg';
import { ReactComponent as IconSettings } from '../../Icons/Sidebar/Settings.svg';
import { ReactComponent as IconSetup } from '../../Icons/Sidebar/Setup.svg';
import NotificationsPanel from './NotificationsPanel';
import './SideBar.scss';
import UserMenuButton from './UserMenuButton';
import { useSelectedGroup } from 'redux/hooks/common';

const modalDefaultState = {
  heading: '',
  content: null,
  show: false,
  showButtons: false,
  okButtonText: '',
  noButtonText: '',
  dialogClassName: '',
  titleClr: '',
  size: FmlxModal.Size.MEDIUM,
  isModalEscapeable: true,
  hideCloseButton: false,
};

interface ISidebarProps {
  isLicenseViolated: boolean;
}

function Sidebar(props: ISidebarProps) {
  const { isLicenseViolated } = props;
  const history = useHistory();
  const USER = 'USER';
  const EXPLORE = 'EXPLORE';
  const HELPANDSUPPORT = 'HELPANDSUPPORT';
  const SETUP = 'SETUP';
  const SETTINGS = 'SETTINGS';
  const [openedNavItem, setOpenedNavItem] = useState({ value: USER });
  const [modalDetails, setModalDetails] = useState(modalDefaultState);
  const [showModal, setShowModal] = useState(false);
  const [updateUser] = useModel(UserGroupService, 'updateUser');
  const selectedGroup = useSelectedGroup();
  const dispatch = useRMDispatch();
  const _setProductEdition = useCallback(
    (productEdition: string) => dispatch(setProductEdition(productEdition)),
    [dispatch],
  );
  const rmLite = useRMLite();
  const logoutUser = useLogoutUser();

  const loggedInUser = getCurrentUser()?.name;

  const onNavItemClick = (navItemId) => {
    if (openedNavItem.value === navItemId) {
      setOpenedNavItem({ value: null });
    } else {
      setOpenedNavItem({ value: navItemId });
    }
    if (navItemId === 'HELPANDSUPPORT') {
      window.open(rmLite ? RMLiteHelpURL : RMHelpURL, 'webhelp');
    }
  };

  useEffect(() => {
    const licenseService = new LicenseService();
    licenseService
      .getLicenseDetails()
      .then((res) => _setProductEdition(res?.licProduct?.edition));
    const user = getCurrentUser();

    if (user?.shouldResetPassword) {
      handleChangePassword(true);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalDetails(modalDefaultState);
  }, [modalDefaultState]);

  const handleEditUserSave = useCallback(async (userDetails: UserDTO) => {
    const updatedUser: UserDTO = await updateUser(userDetails);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'Your Changes Have been saved',
    );
    setShowModal(false);
  }, []);

  const handleEditUserClick = useCallback(() => {
    const user = getCurrentUser();
    user.notificationType = { id: user.notificationType };

    setShowModal(true);
    setModalDetails({
      ...modalDetails,
      show: true,
      heading: 'Edit User',
      content: (
        <UserAddEdit
          user={user}
          closeModal={handleCloseModal}
          handleOnSave={handleEditUserSave}
        />
      ),
      showButtons: false,
      okButtonText: '',
      noButtonText: '',
      dialogClassName: '',
      titleClr: '',
    });
  }, []);

  const removeSelectedLicense = () => {
    removeCookie('selectedLicenseType', 'logout');
    removeCookie('selectedLicenseIndex', 'logout');
    removeCookie('regComputerName', 'logout');
  };

  const handleLogout = useCallback(async () => {
    if (Cookies.get('selectedLicenseType') === 'Seat') {
      if (!Cookies.get('regComputerName')) {
        removeSelectedLicense();
      } else {
        const isSeatRegistered = await new LicenseService().isSeatRegistered();
        if (!isSeatRegistered) {
          removeSelectedLicense();
        }
      }
    }
    logoutUser();
  }, []);

  const handleChangePassword = useCallback(
    (forcePasswordChange: boolean = false) => {
      setShowModal(true);
      setModalDetails({
        ...modalDetails,
        show: true,
        heading: 'Change User Password',
        content: <ChangePassword closeModal={handleCloseModal} />,
        showButtons: false,
        okButtonText: '',
        noButtonText: '',
        isModalEscapeable: !forcePasswordChange,
        hideCloseButton: forcePasswordChange,
      });
    },
    [setModalDetails],
  );

  const handleUserPreferencesClick = useCallback(
    (currentPreference: string, groupId?: number) => {
      const user = getCurrentUser();
      user.notificationType = { id: user.notificationType };

      setShowModal(true);
      setModalDetails({
        show: true,
        heading:
          currentPreference == Preference.UserPreference
            ? 'User Preferences'
            : 'Group Preferences',
        content: <Preferences currentPreference={currentPreference} groupId={groupId} />,
        showButtons: false,
        okButtonText: 'Save',
        noButtonText: 'Discard',
        dialogClassName: 'min-w-[65%] w-[65%]',
        titleClr: '#1B1B1B',
        size: FmlxModal.Size.QUADRUPLE_EXTRA_LARGE,
        isModalEscapeable: true,
        hideCloseButton: false,
      });
    },
    [],
  );

  /**
   * TODO: this needs to be fixed.
   */
  const userPannelList = [
    {
      icon: <IconEditUser id="sidebar-userpanellist-edituser-icon" />,
      text: 'Edit User',
      onClick: handleEditUserClick,
    },
    {
      icon: <IconChangePassword id="sidebar-userpanellist-changepass-icon" />,
      text: 'Change Password',
      onClick: () => handleChangePassword(false),
    },
    {
      icon: (
        <RMSvgIcon
          id="sidebar-userpanellist-userpreferences-icon"
          name="UserPreference"
          fill="black"
          title="User Preference"
        />
      ),
      text: 'User Preferences',
      onClick: () => handleUserPreferencesClick(Preference.UserPreference),
    },
    ...(selectedGroup?.isGroupAdmin
      ? [
          {
            icon: (
              <RMSvgIcon
                id="sidebar-userpanellist-grouppreferences-icon"
                name="GroupPreference"
                fill="black"
                title="Group Preference"
              />
            ),
            text: 'Group Preferences',
            onClick: () =>
              handleUserPreferencesClick(Preference.GroupPreference, selectedGroup?.id),
          },
        ]
      : []),
    {
      icon: <IconLogOff id="sidebar-userpanellist-logout-icon" />,
      text: 'Log Out',
      onClick: handleLogout,
    },
  ];

  const rmLiteUserPannelList = [
    {
      icon: <IconChangePassword id="sidebar-rmlisteuserpanellist-changepass-icon" />,
      text: 'Change Password',
      onClick: () => handleChangePassword(false),
    },
    {
      icon: <IconLogOff id="sidebar-rmlisteuserpanellist-logout-icon" />,
      text: 'Log Out',
      onClick: handleLogout,
    },
  ];

  const setupTab = useSetupTabOption();
  const settingsTab = useSettingsTab();
  const isAdmin = isAdministrator();
  return (
    <div id="sidebar-container" className="sidebar border-sidebar h-100">
      <NotificationsPanel key={'notifications-panel'} />
      <UserMenuButton
        Icon={<IconUser id="sidebar-usermenubutton" />}
        key={'user-menu'}
        title={loggedInUser}
        userPannelButtonsList={rmLite ? rmLiteUserPannelList : userPannelList}
        hideGroups={rmLite}
        selectedGroupId={selectedGroup?.id}
        disabled={isLicenseViolated}
      ></UserMenuButton>
      <NavItem
        to="/project"
        label="Explore"
        key="explore"
        isDisabled={isLicenseViolated}
        icon={<IconExplore id="sidebar-navitem-explore" />}
        onClick={() => onNavItemClick(EXPLORE)}
      />
      {isAdmin && !rmLite ? (
        <NavItem
          to={`/setup/${setupTab}`}
          label="Setup"
          key="setup"
          icon={<IconSetup id="sidebar-navitem-setup" />}
          isDisabled={!isAdmin || isLicenseViolated}
          onClick={() => onNavItemClick(SETUP)}
          activeClass="navItem-active-class"
        />
      ) : null}
      {isAdmin && !rmLite ? (
        <NavItem
          to={`/settings/${settingsTab}`}
          label="Settings"
          key="settings"
          isDisabled={!isAdmin || isLicenseViolated}
          icon={
            <div id="sidebar-admin-container" style={{ height: '18px', width: '18px' }}>
              <IconSettings id="sidebar-navitem-settings" />
            </div>
          }
          onClick={() => onNavItemClick(SETTINGS)}
          activeClass="navItem-active-class"
        />
      ) : null}
      {
        <NavItem
          to={`${history?.location?.pathname ? history?.location?.pathname : ''}`}
          label="Help"
          key="help"
          icon={<RMSvgIcon id="sidebar-navitem-help" name="Help" fill={'#ffffff'} />}
          onClick={() => onNavItemClick(HELPANDSUPPORT)}
        />
      }
      <RMModal
        id="sidebar-show-modal"
        setShowModal={setShowModal}
        showModal={showModal}
        body={modalDetails.content}
        heading={modalDetails.heading}
        showButtons={modalDetails.showButtons}
        okButtonText={modalDetails.okButtonText}
        noButtonText={modalDetails.noButtonText}
        dialogClassName={modalDetails.dialogClassName}
        titleClr={modalDetails.titleClr}
        size={modalDetails.size}
        isModalEscapeable={modalDetails.isModalEscapeable}
        hideCloseButton={modalDetails.hideCloseButton}
      />
    </div>
  );
}
export default Sidebar;
